import React from 'react';
import { useLocation } from 'react-router-dom';
import { KatButton, KatColumn, KatDivider, KatRow } from '@amzn/katal-react';
import initialMetricsPublisher from 'src/metrics';
import { resolveBundleUrlWithFallbacks, usePNFTranslation } from 'src/common/helpers';
import { contactUsFormUrls, ContactUsType, CountryCode, getPageViewForLandingPageUrl, PageViewPNF } from '../urls-config';
import { BrandRegistryInfoAlertBox } from './BrandRegistryInfoAlertBox';
import { AuthenticationState } from './hooks/useTrimContext';

const ReportAViolation = React.lazy(() =>
  import(
    /* webpackChunkName: 'ReportAViolation' */ '@amzn/rav-report-a-violation/dist'
  ).then((library) => ({ default: library.ReportAViolation }))
);
const RetractEscalate = React.lazy(() =>
  import(
    /* webpackChunkName: 'RetractEscalate' */ '@amzn/rav-retract-escalate-components/dist'
  ).then((library) => ({ default: library.RetractEscalate }))
);

interface PublicNoticeFormPageProps {
  authenticationState?: AuthenticationState;
  currentLanguage: string;
  domainCountryCode: CountryCode;
  routerProps: any;
  userFirstName: string;
}

const PnfLandingPage: React.FC<PublicNoticeFormPageProps> = (props: PublicNoticeFormPageProps) => {
  const t = usePNFTranslation();
  const {
    authenticationState,
    domainCountryCode,
    routerProps,
    userFirstName,
  } = props;

  const [isReportStarted, setIsReportStarted] = React.useState(false);

  const location = useLocation();
  const pageView = getPageViewForLandingPageUrl(location.pathname);

  const handleSignOutClick = () => {
    window.location.href = authenticationState?.signOutUrl || '';
  };
  const handleRetractReportClick = () => {
    window.location.href = '/report/infringement/retract';
  };
  const handleStartReportClick = () => {
    setIsReportStarted(true);
  };

  const renderPNFReportForm = PageViewPNF.REPORT === pageView && isReportStarted;

  const contactUsFormUrlSeller = contactUsFormUrls(domainCountryCode, ContactUsType.SELLER);
  const contactUsFormUrlBuyer = contactUsFormUrls(domainCountryCode, ContactUsType.BUYER);
  const ipLinkUrl = contactUsFormUrls(domainCountryCode, ContactUsType.IP);
  const publicNoticeFormInstructions2 = t('public-notice-form-instructions-2', {
    contactUsForm: (chunk: string) =>
      <a
        href={contactUsFormUrlSeller}
        id="pnf-contact-us-form"
        target="_blank"
      >
        {chunk}
      </a>,
  });
  const publicNoticeFormInstructions3 = t('public-notice-form-instructions-3', {
    contactUsForm: (chunk: string) =>
      <a
        href={contactUsFormUrlBuyer}
        id="pnf-contact-us-form-buyer"
        target="_blank"
      >
        {chunk}
      </a>,
  });
  const weDoNotEnforceInstructions2 = t('public-notice-form-we-do-not-enforce-instructions-2', {
    contactUsFormLink: (chunk: string) =>
      <a
        href={contactUsFormUrlSeller}
        id="pnf-not-enforce-contact-us-form"
        target="_blank"
      >
        {chunk}
      </a>,
  });
  const weDoNotEnforceInstructions5 = t('public-notice-form-we-do-not-enforce-instructions-5', {
    ipLink: (chunk: string) =>
      <a
        href={ipLinkUrl}
        id="pnf-ip-link"
        target="_blank"
      >
        {chunk}
      </a>,
  });

  if (renderPNFReportForm) {
    return (
      <ReportAViolation
        key={`report-a-violation-${props.currentLanguage}`}
        metricsPublisher={initialMetricsPublisher}
        resolveBundleUrlFromParent={resolveBundleUrlWithFallbacks}
        {...routerProps}
      />
    );
  }

  return (
    <div className="page-padding">
      {/* Page container for page level padding */}
      <KatRow className="d-flex justify-content-between">
        <KatColumn lg={6} md={6} xs={6}>
          <KatRow className="pt-4">
            <h1>
              {PageViewPNF.RETRACT === pageView
                ? t('pnf-retract-form-title')
                : t('public-notice-form-header')}
            </h1>
          </KatRow>
          {
            PageViewPNF.RETRACT === pageView &&
            <KatRow className="pt-2">
              <h2>{t('pnf-retract-about')}</h2>
              <p>{t('pnf-retract-info')}</p>
              <p>{t('pnf-retract-last-180-days')}</p>
            </KatRow>
          }
          {
            PageViewPNF.REPORT === pageView &&
            <>
              <KatRow className="pt-2">
                <h2>{t('public-notice-form-instructions-title')}</h2>
                <span>{t('public-notice-form-instructions-1')}</span>
                <ul>
                  <li>{publicNoticeFormInstructions2}</li>
                  <li>{publicNoticeFormInstructions3}</li>
                </ul>
              </KatRow>
              <KatRow className="pt-2">
                <h2>{t('public-notice-form-we-do-not-enforce-title')}</h2>
                <p>
                  {t('public-notice-form-we-do-not-enforce-instructions-1', {
                    b: (chunk: string) => <b>{chunk}</b>
                  })}
                </p>
                <p>
                  {weDoNotEnforceInstructions2}
                </p>
                <p>{t('public-notice-form-we-do-not-enforce-instructions-3')}</p>
                <p>
                  {t('public-notice-form-we-do-not-enforce-instructions-4', {
                    b: (chunk: string) => <b>{chunk}</b>
                  })}
                </p>
                <p>
                  {weDoNotEnforceInstructions5}
                </p>
              </KatRow>
            </>
          }
          <KatRow className="pt-1 d-flex justify-content-between">
            <div>
              <KatButton
                id="pnf-website-sign-out-button"
                label={t('public-notice-form-sign-out-button', {
                  givenName: userFirstName,
                })}
                variant="tertiary"
                onClick={handleSignOutClick}
              />
            </div>
            {
              PageViewPNF.REPORT === pageView &&
              <div>
                <KatButton
                  id="pnf-website-retract-report-button"
                  variant="secondary"
                  onClick={handleRetractReportClick}
                >
                  {t('public-notice-form-retract-report')}
                </KatButton>
                <KatButton
                  className="ms-2"
                  id="pnf-website-start-report-button"
                  variant="primary"
                  onClick={handleStartReportClick}
                >
                  {t('public-notice-form-start-report')}
                </KatButton>
              </div>
            }
          </KatRow>
          {
            PageViewPNF.RETRACT === pageView &&
            <div className="pt-3">
              <KatDivider variant="athens" />
              <RetractEscalate {...routerProps} resolveBundleUrlFromParent={resolveBundleUrlWithFallbacks} />
            </div>
          }
        </KatColumn>
        <KatColumn lg={4} md={4} xs={4}>
          {
            domainCountryCode !== CountryCode.CN &&
            <BrandRegistryInfoAlertBox
              domainCountryCode={domainCountryCode}
            />
          }
        </KatColumn>
      </KatRow>
    </div>
  );
};

export default PnfLandingPage;
